import { AppScreen } from "@contentstech/stackflow-plugin-basic-ui/solid";
import { useParent } from "@contentstech/stackflow-plugin-omniflow";
import type { ActivityComponentType } from "@contentstech/stackflow-solid/future";
import { For, type JSX, Match, Show, Suspense, Switch, createMemo, createSignal } from "solid-js";
import { Dynamic } from "solid-js/web";
import { Passthrough } from "~/components/passthrough";
import { AppBar } from "~/components/ui/appbar";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { Image } from "~/components/ui/image";
import { useAddChannel } from "~/lib/actions/addChannel";
import {
  channelControllerDeleteChannelApiV1,
  channelControllerGetChannelsApiV1,
  getChannelControllerGetChannelsApiV1Url,
} from "~/lib/api/__generated__/api.schema";
import { type Channel, ChannelReviewStatus } from "~/lib/api/__generated__/models";
import { environment } from "~/lib/environment";

import Tooltip from "@corvu/tooltip";
import { createMutation, createQuery } from "@tanstack/solid-query";
import { Divider } from "~/components/ui/divider";
import { Drawer, DrawerContent, DrawerTrigger } from "~/components/ui/drawer";
import { cn } from "~/lib/utils";

declare module "@stackflow/config" {
  interface Register {
    ChannelsSettingsActivity: Record<string, never>;
  }
}

const ChannelsSettingsActivity: ActivityComponentType<"ChannelsSettingsActivity"> = () => {
  const [addChannel, addChannelErrorDialog] = useAddChannel();
  const [channelToDelete, setChannelToDelete] = createSignal<Channel | undefined>(undefined);

  const query = createQuery(() => ({
    queryKey: [getChannelControllerGetChannelsApiV1Url()],
    queryFn: async () => channelControllerGetChannelsApiV1(),
  }));

  const mutate = createMutation(() => ({
    mutationFn: async (id: string) => {
      channelControllerDeleteChannelApiV1({ id });
    },
  }));

  const channelToDeleteLatest = createMemo<Channel | undefined>((prev) => {
    const curr = channelToDelete();
    if (curr == null) return prev;
    return curr;
  });
  const parent = useParent();

  return (
    <Dynamic component={parent ? Passthrough : AppScreen}>
      <div class="flex flex-col min-h-full pb-[var(--safe-area-inset-bottom)]">
        <AppBar
          title="Manage channels"
          actions={[
            <Show when={environment === "desktop"}>
              <Button
                variant="highlighted"
                size="sm"
                onClick={async () => {
                  await addChannel();
                  query.refetch();
                }}
              >
                Add channel
              </Button>
            </Show>,
          ]}
          topActions={[
            <Show when={environment === "mobile" && (query.data?.data.items.length ?? 0) > 0}>
              <Button
                variant="highlighted"
                size="sm"
                onClick={async () => {
                  await addChannel();
                  query.refetch();
                }}
              >
                Add channel
              </Button>
            </Show>,
          ]}
        />
        <Suspense>
          <Show
            when={query.data?.data.items.length !== 0}
            fallback={
              <div class="flex-1 flex flex-col gap-30px env-mobile:(items-center justify-center) env-desktop:(items-center mt-120px flex-col-reverse justify-end gap-16px)">
                <div class="flex flex-col gap-8px text-center">
                  <h2 class="prose-md-b text-primary">No channels connected yet</h2>
                  <p class="prose-sm text-secondary">
                    Add your first channel to start managing
                    <br />
                    and tracking your YouTube Shorts
                  </p>
                </div>
                {environment === "desktop" ? (
                  <div class="i-bp-channel-bold size-24px" />
                ) : (
                  <Button variant="highlighted" size="sm" onClick={addChannel}>
                    Add channel
                  </Button>
                )}
              </div>
            }
          >
            <ul class="flex flex-col px-16px my-16px env-desktop:px-32px">
              <For each={query.data?.data.items}>
                {(channel) => (
                  <>
                    <li class="group flex flex-col">
                      <div class="w-full h-0.75px bg-border-light" />
                      <div class="flex items-center py-16px">
                        <div class="flex w-400px">
                          {/* Temporary fallback */}
                          <Suspense
                            fallback={<div class="size-48px rounded-full overflow-hidden mr-12px bg-border-light" />}
                          >
                            <Image
                              src={channel.profileImageUrl}
                              alt={`Channel profile image of ${channel.name}`}
                              class="size-48px rounded-full overflow-hidden mr-12px"
                            />
                          </Suspense>
                          <div class="flex flex-col gap-2px flex-grow-1">
                            <h2 class="prose-md text-black">{channel.name}</h2>
                            <p class="prose-xs text-tertiary">{channel.customUrl}</p>
                            <p class="prose-xs text-tertiary">{channel.subscriberCount} subscribers</p>
                          </div>
                        </div>
                        <Show when={environment === "mobile"}>
                          <div class="flex-1" />
                        </Show>

                        <ChannelReviewInfo
                          target={
                            <div
                              class={cn(
                                "flex gap-4px items-center px-6px py-3px mr-16px rounded-full",
                                channel.reviewStatus === ChannelReviewStatus.PENDING
                                  ? "bg-functional-orange-light"
                                  : channel.reviewStatus === ChannelReviewStatus.REJECTED
                                    ? "bg-functional-red-light"
                                    : "bg-functional-green-light",
                              )}
                            >
                              <Switch>
                                <Match when={channel.reviewStatus === ChannelReviewStatus.PENDING}>
                                  <i class="i-bp-pending text-functional-orange" />
                                  <span class="prose-xs text-functional-orange">Pending</span>
                                </Match>
                                <Match when={channel.reviewStatus === ChannelReviewStatus.REJECTED}>
                                  <i class="i-bp-failed text-functional-red-dark" />
                                  <span class="prose-xs text-functional-red-dark">Denied</span>
                                </Match>
                                <Match when={channel.reviewStatus === ChannelReviewStatus.APPROVED}>
                                  <i class="i-bp-checked text-functional-green-dark" />
                                  <span class="prose-xs text-functional-green-dark">Verified</span>
                                </Match>
                              </Switch>
                            </div>
                          }
                          status={channel.reviewStatus}
                        />
                        <Show when={environment === "desktop"}>
                          <div class="flex-1" />
                        </Show>

                        <Button
                          variant="outline"
                          size="sm"
                          class="env-mobile:border-none env-mobile:p-4px!"
                          onClick={() => setChannelToDelete(channel)}
                        >
                          {environment === "desktop" ? "Delete" : <i class="i-bp-ellipsis text-tertiary" />}
                        </Button>
                      </div>
                      <div class="w-full h-0.75px bg-border-light hidden group-last:block" />
                    </li>
                  </>
                )}
              </For>
            </ul>
          </Show>
        </Suspense>
      </div>
      <Dialog open={channelToDelete() !== undefined} onOpenChange={() => setChannelToDelete(undefined)}>
        <DialogContent class="env-mobile:m-56px">
          <DialogHeader onClose={environment === "desktop" ? () => setChannelToDelete(undefined) : undefined}>
            <DialogTitle>
              {environment === "desktop"
                ? `Delete ‘${channelToDeleteLatest()?.name}’?`
                : `Are you sure you want to delete ‘${channelToDeleteLatest()?.name}’?`}
            </DialogTitle>
          </DialogHeader>
          <p class="text-center env-desktop:text-left prose-sm">
            Deleting your channel will stop earnings from being counted and negatively impact your monetization.
          </p>
          <DialogFooter>
            <Button
              class="h-48px text-white env-desktop:(w-120px h-42px)"
              size="sm"
              onClick={async () => {
                await mutate.mutateAsync(channelToDelete()!.id);
                query.refetch();
                setChannelToDelete(undefined);
              }}
            >
              Delete
            </Button>
            <Button
              variant="text"
              size="sm"
              class="self-center text-tertiary env-desktop:(after:h-0! h-42px)"
              onClick={() => setChannelToDelete(undefined)}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {addChannelErrorDialog()}
    </Dynamic>
  );
};

const ChannelReviewInfo = (props: {
  target: JSX.Element;
  status: ChannelReviewStatus;
}) => {
  const content = () => (
    <div class="flex flex-col">
      <h3 class="prose-sm-b text-primary env-mobile:text-center">
        <Switch>
          <Match when={props.status === ChannelReviewStatus.PENDING}>Verifying Your Channel</Match>
          <Match when={props.status === ChannelReviewStatus.REJECTED}>Channel Not Approved</Match>
        </Switch>
      </h3>
      <Show when={environment === "mobile"}>
        <Divider size="full" class="mx-[-1.5rem] mt-15px" />
      </Show>
      <p class="prose-xs leading-[1.4] text-secondary mt-12px env-mobile:mt-20px">
        <Switch>
          <Match when={props.status === ChannelReviewStatus.PENDING}>
            We're verifying your channel for compliance with internal policies. This review may take up to 24 hours. If
            you don’t hear back, please contact our support team.
          </Match>
          <Match when={props.status === ChannelReviewStatus.REJECTED}>
            Your channel registration has been denied because we found content that does not comply with internal
            policies. If you would like to appeal this decision or have questions, please contact the support team.
          </Match>
        </Switch>
      </p>
    </div>
  );
  if (props.status === ChannelReviewStatus.APPROVED) {
    return props.target;
  }

  return environment === "mobile" ? (
    <Drawer>
      <DrawerTrigger class="flex items-center">{props.target}</DrawerTrigger>
      <DrawerContent>
        <div class="px-16px pt-16px pb-24px">{content()}</div>
      </DrawerContent>
    </Drawer>
  ) : (
    <Tooltip
      placement="bottom-start"
      openDelay={100}
      closeOnPointerDown={false}
      floatingOptions={{
        offset: 13,
        shift: true,
        size: {
          fitViewPort: true,
        },
      }}
    >
      <Tooltip.Anchor class="flex justify-center items-center">
        <Tooltip.Trigger class="flex">{props.target}</Tooltip.Trigger>
      </Tooltip.Anchor>
      <Tooltip.Portal>
        <Tooltip.Content class="relative rounded-12px bg-white z-999 w-280px shadow-[0px_4px_12px_0px_rgba(0,0,0,0.15)] overflow-y-auto">
          <div class="px-16px pb-24px pt-20px">{content()}</div>
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip>
  );
};

export default ChannelsSettingsActivity;
